/**
 * Availability evaluation — timezone-aware schedule checker.
 *
 * Determines whether the business is currently within its configured
 * business hours. Uses the Intl API (supported in all modern browsers)
 * to resolve the current time in the business's timezone without any
 * date library dependency.
 *
 * This module handles ONLY schedule-based evaluation. Real-time agent
 * presence is managed server-side and pushed via the `availability_changed`
 * socket event.
 */

import type {
  AvailabilityConfig,
  WeeklySchedule,
  DaySchedule,
} from '../client/api-types';

/** Map JS Date.getDay() (0=Sun) to our WeeklySchedule keys. */
const DAY_KEYS: (keyof WeeklySchedule)[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

/**
 * Parse an "HH:MM" string into total minutes since midnight.
 * Returns NaN for malformed input (defensive — backend validates).
 */
function parseTimeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Get the current time components in the given IANA timezone.
 * Returns `null` if the timezone is not supported by the browser.
 */
function nowInTimezone(
  timezone: string,
): { dayIndex: number; minutes: number } | null {
  try {
    const now = new Date();
    // Use Intl to get individual parts in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
    const parts = formatter.formatToParts(now);

    const weekdayPart = parts.find((p) => p.type === 'weekday')?.value;
    const hourPart = parts.find((p) => p.type === 'hour')?.value;
    const minutePart = parts.find((p) => p.type === 'minute')?.value;

    if (!weekdayPart || !hourPart || !minutePart) return null;

    const dayMap: Record<string, number> = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };
    const dayIndex = dayMap[weekdayPart];
    if (dayIndex === undefined) return null;

    // Intl hour12:false can return "24" for midnight in some locales
    const hour = Number(hourPart) % 24;
    const minutes = hour * 60 + Number(minutePart);

    return { dayIndex, minutes };
  } catch {
    // Unsupported timezone — fall back to backend status
    return null;
  }
}

/**
 * Check if the current moment falls within the business schedule.
 *
 * Returns:
 * - `true`  if the business is within hours
 * - `false` if outside hours or the day is disabled
 * - `null`  if the schedule/timezone couldn't be evaluated (caller should
 *           fall back to the backend-provided `status`)
 */
export function isWithinSchedule(
  schedule: WeeklySchedule,
  timezone: string,
): boolean | null {
  const time = nowInTimezone(timezone);
  if (!time) return null;

  const dayKey = DAY_KEYS[time.dayIndex];
  const day: DaySchedule = schedule[dayKey];

  if (!day.enabled) return false;

  const start = parseTimeToMinutes(day.start);
  const end = parseTimeToMinutes(day.end);

  if (Number.isNaN(start) || Number.isNaN(end)) return null;

  return time.minutes >= start && time.minutes < end;
}

/**
 * Evaluate the current availability status based on the full config.
 *
 * For modes that include a schedule component, this performs a local
 * timezone-aware check. For presence-only modes, it returns the
 * backend-provided status as-is.
 *
 * @param config  The availability config from the handshake
 * @returns `'online'` or `'offline'`
 */
export function evaluateAvailability(
  config: AvailabilityConfig,
): 'online' | 'offline' {
  switch (config.mode) {
    case 'always':
      return 'online';

    case 'schedule': {
      if (!config.schedule || !config.timezone) return config.status;
      const inSchedule = isWithinSchedule(config.schedule, config.timezone);
      return inSchedule === null
        ? config.status
        : inSchedule
          ? 'online'
          : 'offline';
    }

    case 'presence':
      // Presence is server-side only — trust the backend status
      return config.status;

    case 'schedule_presence': {
      // Must be within schedule AND have agents available.
      // Schedule: check locally. Presence: trust backend status.
      if (!config.schedule || !config.timezone) return config.status;
      const inSchedule = isWithinSchedule(config.schedule, config.timezone);
      if (inSchedule === false) return 'offline';
      // Within schedule (or can't evaluate) — presence decides
      return config.status;
    }

    default:
      return config.status;
  }
}

/**
 * Format the `next_available_at` timestamp into a human-readable time
 * in the visitor's local timezone. Returns null if not available.
 *
 * Examples: "9:00", "14:30", "lunes 9:00"
 */
export function formatNextAvailable(
  isoTimestamp: string | null | undefined,
  locale: string,
): string | null {
  if (!isoTimestamp) return null;

  try {
    const date = new Date(isoTimestamp);
    if (Number.isNaN(date.getTime())) return null;

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    const timeStr = date.toLocaleTimeString(
      locale === 'es' ? 'es-MX' : 'en-US',
      {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
      },
    );

    if (isToday) return timeStr;

    if (isTomorrow) {
      return locale === 'es' ? `mañana ${timeStr}` : `tomorrow ${timeStr}`;
    }

    const dayStr = date.toLocaleDateString(
      locale === 'es' ? 'es-MX' : 'en-US',
      {
        weekday: 'long',
      },
    );
    return `${dayStr} ${timeStr}`;
  } catch {
    return null;
  }
}
